import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, Plus, MapPin, Users, TrendingUp, Upload } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const Events = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const response = await api.get('/events')
      setEvents(response.data || [])
    } catch (error) {
      console.error('Failed to fetch events:', error)
      toast.error('Failed to fetch events.')
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const [showAddModal, setShowAddModal] = useState(false)
  const [newEvent, setNewEvent] = useState({
    name: '',
    type: 'Conference',
    date: '',
    location: '',
    description: '',
    status: 'Upcoming'
  })

  const handleCreateEvent = async (e) => {
    e.preventDefault()
    try {
      await api.post('/events', newEvent)
      setShowAddModal(false)
      setNewEvent({
        name: '',
        type: 'Conference',
        date: '',
        location: '',
        description: '',
        status: 'Upcoming'
      })
      fetchEvents()
    } catch (error) {
      console.error('Failed to create event:', error)
      toast.error('Failed to create event')
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      'Upcoming': 'bg-blue-100 text-blue-700',
      'In Progress': 'bg-green-100 text-green-700',
      'Completed': 'bg-gray-100 text-gray-700',
      'Cancelled': 'bg-red-100 text-red-700'
    }
    return colors[status] || 'bg-gray-100 text-gray-700'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Events</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track conferences, trade shows, and networking events</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Event
        </button>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {events.map((event) => (
          <div
            key={event._id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{event.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                    {event.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{event.type}</p>
              </div>
            </div>

            {event.description && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 line-clamp-2">{event.description}</p>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{format(new Date(event.date), 'MMM dd, yyyy')}</span>
              </div>
              {event.location && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span className="truncate">{event.location}</span>
                </div>
              )}
            </div>

            {/* Event Metrics */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <Users className="w-3 h-3 mr-1" />
                    Contacts
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{event.totalContacts || 0}</p>
                </div>
                <div>
                  <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Qualified
                  </div>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">{event.qualifiedLeads || 0}</p>
                </div>
                <div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Conv. Rate</div>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{event.conversionRate?.toFixed(1) || 0}%</p>
                </div>
              </div>
            </div>

            {/* Attendees Preview */}
            {event.attendees && event.attendees.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Attendees</p>
                <div className="space-y-2">
                  {event.attendees.slice(0, 3).map((attendee, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-gray-900 dark:text-gray-100">{attendee.name}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        attendee.followupStatus === 'Converted' 
                          ? 'bg-green-100 text-green-700'
                          : attendee.followupStatus === 'Contacted'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {attendee.followupStatus}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ROI */}
            {event.roi && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
                <p className="text-xs font-medium text-green-900 dark:text-green-300 mb-1">Event ROI</p>
                <div className="flex justify-between text-sm">
                  <span className="text-green-700 dark:text-green-400">Cost: {event.roi.currency} {event.roi.cost?.toLocaleString()}</span>
                  <span className="text-green-700 dark:text-green-400 font-bold">Revenue: {event.roi.currency} {event.roi.revenue?.toLocaleString()}</span>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button 
                onClick={() => navigate(`/events/${event._id}`)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                View Details
              </button>
              <button 
                onClick={() => navigate(`/events/${event._id}`)}
                className="flex items-center px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30"
              >
                <Upload className="w-4 h-4 mr-1" />
                Add Cards
              </button>
            </div>
          </div>
        ))}

        {events.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No events found</p>
            <button className="mt-4 text-primary-600 hover:underline">
              Create your first event
            </button>
          </div>
        )}
      </div>

      {/* Add Event Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Add New Event</h3>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Name *</label>
                <input
                  type="text"
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({...newEvent, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option>Conference</option>
                  <option>Trade Show</option>
                  <option>Networking</option>
                  <option>Seminar</option>
                  <option>Workshop</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date *</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Create Event
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Events
